import { useCallback, useEffect, useState } from "react";
import { Download, Play } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { saCard, saInput, saLabel } from "../../components/saShared";

export default function SAImpostazioni() {
  const [s, setS] = useState({ grace_period_days: 5, trial_days: 14 });

  const load = useCallback(() => {
    api.get("/superadmin/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/superadmin/settings", {
        grace_period_days: parseInt(s.grace_period_days) || 5,
        trial_days: parseInt(s.trial_days) || 14,
      });
      toast.success("Impostazioni salvate");
    } catch (err) { toast.error(apiError(err)); }
  };

  const lifecycle = async () => {
    try {
      const r = await api.post("/superadmin/lifecycle");
      toast.success(`Lifecycle eseguito: ${r.data.tolleranza} in tolleranza, ${r.data.sospeso} sospesi`);
    } catch (err) { toast.error(apiError(err)); }
  };

  const [exporting, setExporting] = useState(false);
  const exportData = async () => {
    setExporting(true);
    try {
      const r = await api.get("/superadmin/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dishenda-export-${new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "")}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export scaricato");
    } catch (err) { toast.error(apiError(err)); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="sa-impostazioni">
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">Impostazioni piattaforma</h1>

      <form onSubmit={save} className={`${saCard} p-5 space-y-4`}>
        <h2 className="font-display font-semibold text-white">Ciclo di vita account</h2>
        <div>
          <label className={saLabel}>Periodo di tolleranza dopo pagamento fallito (giorni)</label>
          <div className="flex gap-2">
            {[3, 5, 7].map((d) => (
              <button type="button" key={d} onClick={() => setS({ ...s, grace_period_days: d })} data-testid={`grace-${d}`}
                className={`h-10 px-4 rounded-md text-sm font-bold border transition ${parseInt(s.grace_period_days) === d ? "bg-amber-400 text-zinc-950 border-amber-400" : "bg-zinc-800 text-zinc-300 border-zinc-700"}`}>
                {d} giorni
              </button>
            ))}
            <input type="number" min="1" value={s.grace_period_days} onChange={(e) => setS({ ...s, grace_period_days: e.target.value })}
              className={`${saInput} w-24`} data-testid="grace-custom" />
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">Scaduta la tolleranza senza pagamento, l'account passa automaticamente a SOSPESO.</p>
        </div>
        <div>
          <label className={saLabel}>Durata periodo di prova per nuovi ristoranti (giorni)</label>
          <input type="number" min="1" value={s.trial_days} onChange={(e) => setS({ ...s, trial_days: e.target.value })}
            className={saInput} data-testid="trial-days" />
        </div>
        <button className="h-11 px-6 rounded-md bg-amber-400 text-zinc-950 font-bold text-sm" data-testid="sa-settings-save">Salva</button>
      </form>

      <section className={`${saCard} p-5 space-y-3`}>
        <h2 className="font-display font-semibold text-white">Motore lifecycle</h2>
        <p className="text-xs text-zinc-500">Esegue ora le transizioni automatiche: trial scaduti → sospeso, pagamenti falliti → tolleranza, tolleranza scaduta → sospeso. Viene eseguito anche automaticamente a ogni caricamento della dashboard e dal run-due delle automazioni.</p>
        <button onClick={lifecycle} data-testid="sa-run-lifecycle"
          className="h-11 px-4 rounded-md bg-zinc-800 text-zinc-100 text-sm font-bold flex items-center gap-2 border border-zinc-700">
          <Play size={15} weight="bold" /> Esegui lifecycle ora
        </button>
      </section>

      <section className={`${saCard} p-5 space-y-3`}>
        <h2 className="font-display font-semibold text-white">Esporta dati</h2>
        <p className="text-xs text-zinc-500">
          Scarica un file ZIP con tutti i dati della piattaforma (tutti i ristoranti): un JSON per collezione
          compatibile con <code className="text-amber-400">mongoimport</code> per la migrazione, CSV leggibili di
          clienti e prenotazioni, e un manifest con i conteggi. Operazione di sola lettura: nessun dato viene
          modificato. Gli hash delle password sono esclusi per sicurezza.
        </p>
        <button onClick={exportData} disabled={exporting} data-testid="sa-export-btn"
          className="h-11 px-4 rounded-md bg-zinc-800 text-zinc-100 text-sm font-bold flex items-center gap-2 border border-zinc-700 disabled:opacity-50">
          <Download size={15} weight="bold" /> {exporting ? "Preparazione export..." : "Scarica export completo (.zip)"}
        </button>
      </section>

      <section className={`${saCard} p-5`}>
        <h2 className="font-display font-semibold text-white mb-2">Stripe</h2>
        <p className="text-xs text-zinc-500">
          Modalità MOCK attiva: nessuna chiamata reale a Stripe. Webhook predisposto su <code className="text-amber-400">POST /api/webhooks/stripe</code> con
          idempotenza su event_id. Per andare live: implementare un BillingService Stripe in <code className="text-amber-400">backend/billing.py</code> e registrarlo con set_billing_service().
        </p>
      </section>
    </div>
  );
}
